/**
 * Mobile entry point — mirrors main.ts but wires the mobile-specific UI.
 */

import "./ui/styles.css";
import "./ui/mobile.css";

import { AudioEngine } from "./audio/sources";
import { Renderer } from "./viz/renderer";
import { initMobileUI } from "./ui/mobile-ui";
import {
  loadRunningState,
  readPresetFromLocation,
  saveRunningState,
} from "./presets/store";
import { registerPwa } from "./pwa";
import { BackgroundRenderer } from "./viz/background";
import { applyScenePreset } from "./viz/scenes/applyScenePreset";
import { SCENES } from "./viz/scenes/scenePresets";

function bootstrap(): void {
  const canvas = document.getElementById("viz");
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error("Missing #viz canvas element.");
  }

  const engine = new AudioEngine();
  const renderer = new Renderer(canvas);

  // Restore the complete renderer identity so alternate scenes survive reload.
  const runningState = loadRunningState();
  Object.assign(renderer.params, runningState.params);
  renderer.sceneType = runningState.sceneType;

  const shared = readPresetFromLocation();
  if (shared) {
    Object.assign(renderer.params, shared.params);
    renderer.sceneType = shared.sceneType;
  }

  // ---- Auto-detect quality tier on first launch ----
  // Phones get "mobile" (30K particles) by default; everything else stays
  // at whatever loadRunningParams returned (default "standard"). The user
  // can override anytime via the Performance section in the Look tab.
  if (!localStorage.getItem("pfx_qualityTier_set")) {
    const ua = navigator.userAgent.toLowerCase();
    const isPhone = /iphone|android.*mobile/.test(ua);
    if (isPhone) renderer.params.qualityTier = "mobile";
    localStorage.setItem("pfx_qualityTier_set", "1");
  }

  // Auto-save every second and on tab close.
  const saveInterval = window.setInterval(
    () => saveRunningState(renderer.sceneType, renderer.params),
    1000,
  );

  // ---- Backdrop + HUD tick ----
  const backdropEl = document.getElementById("backdrop");
  const background = backdropEl ? new BackgroundRenderer(backdropEl) : null;
  const hudBpm = document.getElementById("hudBpm");
  const hudBass = document.getElementById("hudBass");
  const hudMid = document.getElementById("hudMid");
  const hudTreble = document.getElementById("hudTreble");

  let disposed = false;
  let hudRafId: number | null = null;
  let hudTimer: number | null = null;
  const scheduleTick = (): void => {
    if (disposed) return;
    if (document.hidden) hudTimer = window.setTimeout(tick, 1000 / 30);
    else hudRafId = requestAnimationFrame(tick);
  };
  const tick = (): void => {
    hudRafId = null;
    hudTimer = null;
    if (disposed) return;
    const f = engine.read();
    renderer.setFeatures(f);

    if (backdropEl) {
      const bd = renderer.backdrop;
      if (background) {
        background.tick(f, bd);
      } else {
        backdropEl.style.background = `radial-gradient(ellipse at 50% 45%, rgba(${bd.r}, ${bd.g}, ${bd.b}, ${bd.alpha.toFixed(3)}) 0%, #010104 70%, #000 100%)`;
        backdropEl.style.opacity = String(Math.min(1, bd.alpha + 0.25));
        backdropEl.style.filter = `blur(${bd.blurPx}px)`;
      }
    }

    if (hudBpm) {
      hudBpm.textContent = f.bpm > 0 ? Math.round(f.bpm).toString() : "---";
      hudBpm.classList.toggle("beat", f.beat > 0.4);
    }
    if (hudBass) hudBass.style.setProperty("--lvl", `${Math.round(f.bass * 100)}%`);
    if (hudMid) hudMid.style.setProperty("--lvl", `${Math.round(f.mid * 100)}%`);
    if (hudTreble) hudTreble.style.setProperty("--lvl", `${Math.round(f.treble * 100)}%`);

    scheduleTick();
  };
  scheduleTick();

  renderer.start();
  initMobileUI({ engine, renderer, background });

  if (new URLSearchParams(window.location.search).get("sceneAudit") === "1") {
    const auditWindow = window as typeof window & {
      __pulsefxMobileSceneAudit?: {
        listScenes(): Array<{ name: string; sceneType: string }>;
        selectScene(name: string): boolean;
        setParam(key: string, value: number | boolean | string): boolean;
        renderFrame(): void;
        getState(): {
          sceneType: string;
          frameMs: number;
          renderer: ReturnType<Renderer["getDiagnostics"]>;
        };
      };
    };
    auditWindow.__pulsefxMobileSceneAudit = {
      listScenes: () => SCENES.map((scene) => ({
        name: scene.name,
        sceneType: scene.sceneType ?? "bioclast",
      })),
      selectScene: (name) => {
        const scene = SCENES.find((candidate) => candidate.name === name);
        if (!scene) return false;
        renderer.setCameraControlMode("auto");
        applyScenePreset(renderer, scene);
        return true;
      },
      setParam: (key, value) => {
        if (!(key in renderer.params)) return false;
        (renderer.params as unknown as Record<string, unknown>)[key] = value;
        return true;
      },
      renderFrame: () => {
        (renderer as unknown as { draw(): void }).draw();
      },
      getState: () => ({
        sceneType: renderer.sceneType,
        frameMs: renderer.frameMs,
        renderer: renderer.getDiagnostics(),
      }),
    };
  }

  window.addEventListener("pagehide", (event: PageTransitionEvent) => {
    saveRunningState(renderer.sceneType, renderer.params);
    if (event.persisted || disposed) return;
    disposed = true;
    window.clearInterval(saveInterval);
    if (hudRafId !== null) cancelAnimationFrame(hudRafId);
    if (hudTimer !== null) window.clearTimeout(hudTimer);
    renderer.dispose();
    engine.dispose();
  });
}

bootstrap();
registerPwa();
